import { useState } from "react";
import type { EmailSettings } from "../../../shared/goals/email";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import type { ReadyView } from "../../lib/readyApi";

/**
 * When and how fast cold email goes out, in Get ready: each market's clock (its own local time) and the
 * pace for all markets. Changing either is a new version of the goal. A market already given to Smartlead
 * keeps what it started with.
 */

export const ZONES: [string, string][] = [
  ["America/New_York", "New York (Eastern)"],
  ["America/Chicago", "Chicago (Central)"],
  ["America/Denver", "Denver (Mountain)"],
  ["America/Phoenix", "Phoenix (Arizona)"],
  ["America/Los_Angeles", "Los Angeles (Pacific)"],
  ["America/Anchorage", "Anchorage (Alaska)"],
  ["Pacific/Honolulu", "Honolulu (Hawaii)"],
];

/** "New York" for America/New_York; the last part of any other zone name. */
export const zoneName = (tz: string) => (ZONES.find(([z]) => z === tz)?.[1] ?? tz.split("/").at(-1)!.replace(/_/g, " ")).replace(/ \(.*\)$/, "");

const DAYS: [EmailSettings["days"][number], string][] = [
  ["mon", "Mon"],
  ["tue", "Tue"],
  ["wed", "Wed"],
  ["thu", "Thu"],
  ["fri", "Fri"],
  ["sat", "Sat"],
  ["sun", "Sun"],
];

export const daysText = (d: string[]) => {
  const on = DAYS.filter(([k]) => d.includes(k)).map(([, t]) => t);
  return on.length === 5 && !d.includes("sat") && !d.includes("sun") ? "Mon to Fri" : on.join(", ");
};

export type ChangeSending = (patch: Partial<Pick<EmailSettings, "days" | "startHour" | "endHour" | "newPerDay" | "minTimeBetweenEmails" | "timezones">>, line: string) => Promise<boolean>;

/** A market's clock, with Change while nothing has gone to Smartlead for it yet. */
export function MarketClock({ market, tz, open, timezones, change }: { market: string; tz: ReadyView["lists"][number]["sendTimezone"]; open: boolean; timezones: Record<string, string>; change: ChangeSending }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(tz.timezone);
  const why = tz.source === "goal" ? " (the goal's own time zone)" : tz.check ? ". Part of this state keeps another time zone, so check it." : "";
  if (!editing)
    return (
      <p>
        Sends on {zoneName(tz.timezone)} time{why}
        {open && (
          <>
            {" "}
            <button type="button" className="chip link" onClick={() => setEditing(true)}>
              Change
            </button>
          </>
        )}
      </p>
    );
  const options = ZONES.some(([z]) => z === tz.timezone) ? ZONES : [[tz.timezone, zoneName(tz.timezone)] as [string, string], ...ZONES];
  return (
    <div className="rd-acts">
      <select className="field" aria-label={`Time zone for ${market}`} value={pick} onChange={(e) => setPick(e.target.value)}>
        {options.map(([z, label]) => (
          <option key={z} value={z}>
            {label}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn sm"
        onClick={async () => {
          if (pick !== tz.timezone && !(await change({ timezones: { ...timezones, [market]: pick } }, `${market} sends on ${zoneName(pick)} time.`))) return;
          setEditing(false);
        }}
      >
        Save
      </button>
      <button type="button" className="btn sm ghost" onClick={() => setEditing(false)}>
        Cancel
      </button>
    </div>
  );
}

/** The pace for every market: days, hours (each market's own time), new people a day, minutes between sends. */
export function SendingPace({ sending, open, change }: { sending: NonNullable<ReadyView["sending"]>; open: boolean; change: ChangeSending }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ ...sending });
  const [error, setError] = useState<string | null>(null);
  const text = `${daysText(sending.days)}, ${sending.startHour} to ${sending.endHour} in each market's own time. ${sending.newPerDay} new ${sending.newPerDay === 1 ? "person" : "people"} a day per market, ${sending.minTimeBetweenEmails} minutes apart.`;
  if (!editing)
    return (
      <p>
        {text}
        {open && (
          <>
            {" "}
            <button
              type="button"
              className="chip link"
              onClick={() => {
                setF({ ...sending });
                setEditing(true);
              }}
            >
              Change
            </button>
          </>
        )}
      </p>
    );
  const save = async () => {
    if (!f.days.length) return setError("Pick at least one day.");
    if (f.endHour <= f.startHour) return setError("The end has to be after the start.");
    if (!(f.newPerDay >= 1 && f.newPerDay <= 500)) return setError("New people a day is 1 to 500.");
    if (!(f.minTimeBetweenEmails >= 1 && f.minTimeBetweenEmails <= 1440)) return setError("Minutes apart is 1 to 1440.");
    setError(null);
    const ok = await change(
      { days: f.days, startHour: f.startHour, endHour: f.endHour, newPerDay: f.newPerDay, minTimeBetweenEmails: f.minTimeBetweenEmails },
      `Cold email sends ${daysText(f.days)}, ${f.startHour} to ${f.endHour}, ${f.newPerDay} new a day, ${f.minTimeBetweenEmails} minutes apart.`,
    );
    if (ok) setEditing(false);
  };
  return (
    <div className="rd-form">
      <div className="rd-acts" role="group" aria-label="Days to send">
        {DAYS.map(([k, t]) => (
          <button
            key={k}
            type="button"
            className={`btn sm ${f.days.includes(k) ? "" : "ghost"}`}
            aria-pressed={f.days.includes(k)}
            onClick={() => setF({ ...f, days: f.days.includes(k) ? f.days.filter((d) => d !== k) : [...f.days, k] })}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="rd-acts">
        <label>
          From <input className="field" type="time" value={f.startHour} onChange={(e) => setF({ ...f, startHour: e.target.value })} />
        </label>
        <label>
          to <input className="field" type="time" value={f.endHour} onChange={(e) => setF({ ...f, endHour: e.target.value })} />
        </label>
      </div>
      <div className="rd-acts">
        <label>
          New people a day <input className="field" type="number" min={1} max={500} value={f.newPerDay} onChange={(e) => setF({ ...f, newPerDay: Number(e.target.value) })} />
        </label>
        <label>
          Minutes apart <input className="field" type="number" min={1} max={1440} value={f.minTimeBetweenEmails} onChange={(e) => setF({ ...f, minTimeBetweenEmails: Number(e.target.value) })} />
        </label>
      </div>
      {error && <p className="rd-p rd-stop">{error}</p>}
      <div className="rd-acts">
        <button type="button" className="btn sm" onClick={() => void save()}>
          Save
        </button>
        <button type="button" className="btn sm ghost" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A number with a step down and up, as a row: the phone's stepper. */
export function Stepper({ label, sub, value, shown, min, max, by = 1, onChange }: { label: string; sub?: string; value: number; shown?: string; min: number; max: number; by?: number; onChange: (n: number) => void }) {
  return (
    <div className="ad-stp">
      <span className="m">
        <span className="t">{label}</span>
        {sub && <span className="s">{sub}</span>}
      </span>
      <span className="ad-stpc">
        <button type="button" className="ad-sb" aria-label={`Fewer: ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - by))}>
          −
        </button>
        <b>{shown ?? value}</b>
        <button type="button" className="ad-sb" aria-label={`More: ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + by))}>
          +
        </button>
      </span>
    </div>
  );
}

/** The pace for every market as a sheet: day toggles, the hours, and steppers. Saving is a new version of the goal. */
export function SendingSheet({ open, onClose, sending, markets, change }: { open: boolean; onClose: () => void; sending: NonNullable<ReadyView["sending"]>; markets: string; change: ChangeSending }) {
  const [f, setF] = useState({ ...sending });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [was, setWas] = useState(open);
  if (open !== was) {
    setWas(open);
    if (open) {
      setF({ ...sending });
      setError(null);
    }
  }
  const save = async () => {
    if (!f.days.length) return setError("Pick at least one day.");
    if (f.endHour <= f.startHour) return setError("The end has to be after the start.");
    setError(null);
    setBusy(true);
    const ok = await change(
      { days: f.days, startHour: f.startHour, endHour: f.endHour, newPerDay: f.newPerDay, minTimeBetweenEmails: f.minTimeBetweenEmails },
      `Cold email sends ${daysText(f.days)}, ${f.startHour} to ${f.endHour}, ${f.newPerDay} new a day, ${f.minTimeBetweenEmails} minutes apart.`,
    );
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Sending"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      right={
        <button type="button" className="pn-tbtn strong" disabled={busy} onClick={() => void save()}>
          Save
        </button>
      }
    >
      <p className="ad-shs">In each market's own time{markets ? `: ${markets}` : ""}.</p>
      <div className="pn-cap">Days</div>
      <div className="ad-days" role="group" aria-label="Days to send">
        {DAYS.map(([k, t]) => {
          const on = f.days.includes(k);
          return (
            <button key={k} type="button" className={on ? "ad-day on" : "ad-day"} aria-pressed={on} onClick={() => setF({ ...f, days: on ? f.days.filter((d) => d !== k) : [...f.days, k] })}>
              {t}
            </button>
          );
        })}
      </div>
      <div className="pn-cap ad-mt">Hours</div>
      <div className="pn-grp">
        <label className="pn-irow">
          <span className="ad-irl">From</span>
          <input type="time" value={f.startHour} onChange={(e) => setF({ ...f, startHour: e.target.value })} />
        </label>
        <label className="pn-irow">
          <span className="ad-irl">To</span>
          <input type="time" value={f.endHour} onChange={(e) => setF({ ...f, endHour: e.target.value })} />
        </label>
      </div>
      <div className="pn-cap ad-mt">Pace</div>
      <div className="pn-grp">
        <Stepper label="New people a day" sub="Per market" value={f.newPerDay} min={1} max={500} onChange={(n) => setF({ ...f, newPerDay: n })} />
        <Stepper label="Minutes apart" sub="Between two emails" value={f.minTimeBetweenEmails} min={1} max={1440} by={5} onChange={(n) => setF({ ...f, minTimeBetweenEmails: n })} />
      </div>
      {error && <p className="pn-note ad-bad">{error}</p>}
    </Sheet>
  );
}

/** A market's clock as a list to pick from, while nothing has gone to Smartlead for it yet. */
export function ZoneSheet({ market, tz, timezones, change, onClose }: { market: string | null; tz: string; timezones: Record<string, string>; change: ChangeSending; onClose: () => void }) {
  const options = ZONES.some(([z]) => z === tz) ? ZONES : [[tz, zoneName(tz)] as [string, string], ...ZONES];
  return (
    <Sheet open={market !== null} onClose={onClose} title={market ? `${market.split(",")[0]} sends on` : ""}>
      <div className="pn-grp">
        {options.map(([z, label]) => (
          <button
            key={z}
            type="button"
            className="pn-row"
            onClick={async () => {
              if (market && z !== tz && !(await change({ timezones: { ...timezones, [market]: z } }, `${market} sends on ${zoneName(z)} time.`))) return;
              onClose();
            }}
          >
            <span className="m">
              <span className="t">{label}</span>
            </span>
            {z === tz && (
              <span className="ad-tick">
                <Icon name="check" />
              </span>
            )}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
